import { Check, Copy, Download } from "lucide-react";
import { useMemo, useState } from "react";

import { downloadCsv, SearchField } from "../components/DataKit";
import { friendlyError } from "../lib/friendlyError";
import { trpc } from "../trpc";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

const buttonClass =
  "inline-flex min-h-11 items-center gap-2 rounded-full border border-[color:var(--color-line-strong)] px-5 text-label font-semibold text-[color:var(--color-ink)] transition-colors hover:border-[color:var(--color-ink)]";

/**
 * The Evergrowing waiting list: emails left on the app's "coming soon"
 * landing page, newest first. Read straight from the Evergrowing D1 via
 * the VY backend (organisers only). A one-line count up top, then a
 * searchable table with copy and download for a mail tool at launch.
 *
 * Auto-refreshes every 30s while focused.
 */
export function AppInterest() {
  const query = trpc.appInterestList.useQuery(undefined, {
    retry: false,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
  });

  const [search, setSearch] = useState("");
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  const signups = useMemo(() => query.data?.signups ?? [], [query.data]);

  const weekCount = useMemo(() => {
    const cutoff = Date.now() - WEEK_MS;
    return signups.filter((s) => {
      const t = new Date(s.createdAt).getTime();
      return Number.isFinite(t) && t >= cutoff;
    }).length;
  }, [signups]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      return signups;
    }
    return signups.filter(
      (s) =>
        s.email.toLowerCase().includes(q) ||
        (s.source ?? "").toLowerCase().includes(q),
    );
  }, [signups, search]);

  async function copyEmails() {
    const emails = rows.map((s) => s.email).join(", ");
    try {
      await navigator.clipboard.writeText(emails);
      setCopyFailed(false);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard blocked (an older browser or a page without permission):
      // say so, or they paste nothing into their mail tool and don't know why.
      setCopied(false);
      setCopyFailed(true);
      window.setTimeout(() => setCopyFailed(false), 6000);
    }
  }

  return (
    <section className="mx-auto max-w-[1100px] px-5 py-10 md:px-10 md:py-12">
      <header className="border-b border-[color:var(--color-line)] pb-6">
        <h1 className="page-title">Evergrowing waiting list</h1>
        <p className="mt-3 max-w-xl text-body text-[color:var(--color-ink-soft)]">
          People who asked to hear when the Evergrowing app opens.
        </p>
      </header>

      {query.isLoading ? (
        <p className="mt-8 text-body text-[color:var(--color-ink-soft)]">
          Loading the waiting list…
        </p>
      ) : null}

      {query.error ? (
        <p className="mt-8 text-body text-[color:var(--color-danger)]">
          Couldn&apos;t load the waiting list.{" "}
          {friendlyError(
            query.error,
            "Check your internet connection and try again.",
          )}
        </p>
      ) : null}

      {query.data ? (
        <>
          <p className="mt-8 text-lead text-[color:var(--color-ink)] tabular-nums">
            {signups.length} {signups.length === 1 ? "person" : "people"} on the
            list · {weekCount} joined in the last 7 days
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <SearchField
              value={search}
              onChange={setSearch}
              placeholder="Search by email or where they signed up"
            />
            {signups.length > 0 ? (
              <>
                <button
                  type="button"
                  onClick={() => void copyEmails()}
                  className={buttonClass}
                >
                  {copied ? (
                    <Check size={16} aria-hidden="true" />
                  ) : (
                    <Copy size={16} aria-hidden="true" />
                  )}
                  {copied
                    ? "Copied"
                    : rows.length === signups.length
                      ? "Copy all emails"
                      : `Copy ${rows.length} ${rows.length === 1 ? "email" : "emails"}`}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    downloadCsv(
                      "evergrowing-waiting-list.csv",
                      ["Email", "Where they signed up", "Signed up"],
                      rows.map((s) => [s.email, s.source ?? "", s.createdAt]),
                    )
                  }
                  className={buttonClass}
                >
                  <Download size={16} aria-hidden="true" />
                  Download list
                </button>
              </>
            ) : null}
          </div>
          {copyFailed ? (
            <p
              role="alert"
              className="mt-2 text-label text-[color:var(--color-danger)]"
            >
              Couldn&apos;t copy. Use Download list instead.
            </p>
          ) : null}

          <div className="mt-6 overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="border-b border-[color:var(--color-line-strong)]">
                  <th scope="col" className="table-head py-3 pr-6">
                    Email
                  </th>
                  <th scope="col" className="table-head py-3 pr-6">
                    Where they signed up
                  </th>
                  <th scope="col" className="table-head py-3 pr-6">
                    Signed up
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="empty-state">
                      {signups.length === 0
                        ? "Nobody on the list yet."
                        : "Nothing matches that search."}
                    </td>
                  </tr>
                ) : (
                  rows.map((s) => (
                    <tr
                      key={`${s.email}-${s.createdAt}`}
                      className="border-b border-[color:var(--color-line)] transition-colors hover:bg-[color:var(--color-paper)]"
                    >
                      <td className="py-3 pr-6 text-body font-medium break-all text-[color:var(--color-ink)]">
                        {s.email}
                      </td>
                      <td
                        className={`py-3 pr-6 text-label ${
                          s.source
                            ? "text-[color:var(--color-ink-soft)]"
                            : "text-[color:var(--color-ink-faint)]"
                        }`}
                      >
                        {s.source ?? "Not recorded"}
                      </td>
                      <td className="py-3 pr-6 text-label whitespace-nowrap text-[color:var(--color-ink-soft)] tabular-nums">
                        {formatDate(s.createdAt)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </section>
  );
}
